'use client'

// The slide-up shell every drawer shares.
//
// The wallet and the shrine each grew their own copy of this: same scrim,
// same cream panel, same stitch inset, same 320ms overshoot, same handle,
// same max-w-5xl header sitting over the first card. Three copies is where
// drift starts, and Sam has already had to say "the title is far over on
// the left" once because two of them disagreed.
//
// Structure only. What goes inside stays each drawer's own business.

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { kawaii, ui } from '../../lib/ui/theme'

export function Drawer({
  onClose,
  title,
  subtitle = null,
  // A drawer with tabs has no room for a title and a strapline as well, and
  // the tabs say what the title would have. Passed in rather than built here
  // so Drawer stays a shell and does not learn what a tab is.
  header = null,
  right = null,
  // 86dvh, was 78: Sam kept finding drawers that clipped their content
  // while the top fifth of the screen sat empty. The world still peeks
  // over the top, which is the whole point of a drawer over a page.
  height = 'min(86dvh, 900px)',
  bodyClass = 'kawaii-scroll flex-1 min-h-0 overflow-y-auto px-6 sm:px-10 pb-14 pt-1',
  // Stacked drawers: a second one opens ON TOP of the first and stops
  // short of it, so the drawer underneath is still legible behind. Its
  // own scrim dims what it covers, which is what says which one is live.
  level = 0,
  children,
}) {
  // One frame closed, then open, so the transition actually runs.
  const [entered, setEntered] = useState(false)
  useEffect(() => {
    const t = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(t)
  }, [])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Through a portal, always. A drawer opened from INSIDE another drawer
  // renders inside that drawer's panel, and the panel carries a transform
  // for its slide-up, which makes it the containing block for anything
  // fixed underneath it. The second drawer then positions itself against
  // the first and lands somewhere off the bottom of the screen.
  // 55, not 50: the HUD chrome (clock, menu root) sits at z-50, and at
  // equal z the clock won on DOM order and floated over the journal's
  // header on phones. Drawers are above-world UI; everything HUD stays
  // beneath them.
  const frame = (
    <div className="fixed inset-0 pointer-events-auto" style={{ zIndex: 55 + level * 10 }}>
      {/* A light scrim, not a blackout: the world above the drawer is the
          point of keeping it visible. Click closes. */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(26,16,10,0.28)', opacity: entered ? 1 : 0, transition: 'opacity 300ms ease' }}
        onClick={onClose}
      />

      <div
        className="absolute inset-x-0 bottom-0 flex flex-col"
        style={{
          height,
          background: kawaii.cream,
          borderTop: kawaii.outline,
          borderRadius: '22px 22px 0 0',
          boxShadow: '0 -12px 40px rgba(26,16,10,0.35)',
          fontFamily: ui.font,
          transform: entered ? 'translateY(0)' : 'translateY(102%)',
          transition: 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <div style={{ position: 'absolute', inset: 6, bottom: 0, border: kawaii.stitch, borderBottom: 'none', borderRadius: '18px 18px 0 0', pointerEvents: 'none' }} />

        <div className="shrink-0 pt-2.5 pb-3 px-6 sm:px-10">
          <div className="mx-auto w-10 h-1.5 rounded-full mb-3" style={{ background: 'rgba(47,38,80,0.22)' }} />
          {/* Same max-width and centring as the body, so the title sits over
              the first card rather than out at the edge. */}
          <div className="mx-auto w-full max-w-5xl flex items-start justify-between gap-4">
            <div className="min-w-0">
              {header || (
                <>
                  <span className="flex items-baseline gap-3 flex-wrap">
                    <span style={{ color: kawaii.ink, fontSize: 19, fontWeight: 800 }}>{title}</span>
                    {right}
                  </span>
                  {subtitle && (
                    <span className="block mt-1" style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600 }}>
                      {subtitle}
                    </span>
                  )}
                </>
              )}
            </div>
            <button
              onClick={onClose}
              aria-label="close"
              className="shrink-0 transition-colors"
              style={{ color: kawaii.ink, fontSize: 17, fontWeight: 800, padding: '2px 8px' }}
            >
              ✕
            </button>
          </div>
        </div>

        <div className={bodyClass}>{children}</div>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return frame
  return createPortal(frame, document.body)
}

// ── The furniture that goes inside one ──────────────────────────────────

/** A share control that looks like a button: the X mark, then the word.
 *  Everything shareable uses this one so they all feel like the same
 *  affordance rather than a scatter of underlined words. */
export function XShareButton({ href, label = 'share', style = {} }) {
  return (
    <button
      type="button"
      onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        background: kawaii.cream,
        border: kawaii.outlineThin,
        borderRadius: 999,
        boxShadow: kawaii.dropSm,
        color: kawaii.ink,
        padding: '5px 14px',
        fontSize: 12.5,
        fontWeight: 800,
        cursor: 'pointer',
        ...style,
      }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
        />
      </svg>
      {label}
    </button>
  )
}


/** A cream card with the outline and the solid offset shadow. */
export function Card({ children, className = '', style = {} }) {
  return (
    <div
      className={className}
      style={{
        background: kawaii.cream,
        border: kawaii.outlineThin,
        borderRadius: 18,
        boxShadow: kawaii.dropSm,
        padding: '14px 16px',
        // The card is always cream, so ink is the only sane default. Without
        // it anything that did not set its own colour inherited the page's
        // near-white and vanished: the calendar's event titles were rendering
        // at #ededed on #f7e7d2 and nobody could see them.
        color: kawaii.ink,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/**
 * Heavy-bordered pill, the same construction as the world clock.
 *
 * Text colour is picked from the background rather than always ink: the
 * tier colours are dark (they were chosen as text ON cream) and ink on top
 * of them measures 1.8:1, which is why the rank pill in the shrine drawer
 * has been close to unreadable. Anything darker than mid grey gets cream.
 */
export function Pill({ children, bg = '#f0b064', color = null }) {
  return (
    <span
      style={{
        display: 'inline-block',
        background: bg,
        color: color || (isDark(bg) ? kawaii.cream : kawaii.ink),
        border: kawaii.outlineThin,
        borderRadius: 999,
        boxShadow: kawaii.dropSm,
        padding: '3px 13px',
        fontSize: 12.5,
        fontWeight: 800,
        letterSpacing: '0.01em',
        lineHeight: 1.45,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  )
}

/** Perceived lightness of a #rrggbb, the sRGB way. */
export function isDark(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''))
  if (!m) return false
  const n = parseInt(m[1], 16)
  const lin = (v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)
  const l = 0.2126 * lin(((n >> 16) & 255) / 255)
    + 0.7152 * lin(((n >> 8) & 255) / 255)
    + 0.0722 * lin((n & 255) / 255)
  return l < 0.35
}

/** The needs-card meter: creamDeep trough, ink outline, glossy fill. */
export function Meter({ pct, color = '#f0b064', height = 12 }) {
  return (
    <div
      style={{
        position: 'relative',
        height,
        borderRadius: 999,
        background: kawaii.creamDeep,
        border: kawaii.outlineThin,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          right: 'auto',
          width: `${Math.max(0, Math.min(100, pct * 100))}%`,
          background: color,
          borderRadius: 999,
          backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.45), rgba(255,255,255,0) 60%)',
          transition: 'width 600ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      />
    </div>
  )
}

/** The small caps label that heads a column. */
export function ColumnHeading({ children }) {
  return (
    <div className="mb-4">
      <span style={{ color: 'rgba(47,38,80,0.55)', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em' }}>
        {children}
      </span>
    </div>
  )
}

export default Drawer
